import { UIDateElement } from './ui-dateelement';
import { updateElement } from '../../global/ui-helpers';
import { Labels } from '../../global/labels';
import { Digest } from '../../global/digest';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { ObservedAttributes } from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { UITimePicker } from './ui-timepicker';
import type { NullOr, Optional } from '../../global/utils.type';
import styles from './ui-timerange.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIDateElement}
 * @alias UITimeRange
 * @element ui-timerange
 * @classdesc Represents a class for <code>ui-timerange</code> element.
 * Input two ui-timepickers to select time range.
 * @property {string} [controlId] {@attr control-id} Base id for the time range group and its timepickers.
 *
 * Host element id resolution:
 * - If you set id directly on <ui-timerange>, it is preserved and used as the base for deriving child ids.
 *   Note: This only affects inputs that don't already have a user-set id. Existing user-set input ids
 *   are never overwritten.
 * - If no host id is set and control-id is provided, the host gets control-id.
 * - If no host id is set and no control-id is provided, a base id is generated.
 *
 * Child timepicker input id resolution:
 * - User-set ids are never overwritten. If you set an id on an input (e.g. id="timerange-from"),
 *   that value is preserved, regardless of whether a host id or control-id is set.
 * - If an input has no id, a derived id is applied: first picker `${baseId}-from`, second `${baseId}-to`,
 *   where baseId is the host's id (user-set, control-id, or generated).
 * - The group element always gets an id (user-set, control-id, or generated) so that aria-labelledby
 *   works correctly when the component is used inside a ui-field.
 * @property {string} [valueType="time"] Type of value in this case is 'time' only.
 * @property {UITimePicker} pickerFrom Shortcut to timepicker-from element.
 * @property {UITimePicker} pickerTo Shortcut to timepicker-to element.
 * @slot {@type "ui-timepicker"}
 * @example
 * <ui-timerange min="08:00" max="19:00">
 *   <ui-timepicker>
 *     <input name="time-from" type="time">
 *   </ui-timepicker>
 *   <ui-timepicker>
 *     <input name="time-to" type="time">
 *   </ui-timepicker>
 * </ui-timerange>
 */
@Component({ tag: 'ui-timerange', styles })
export class UITimeRange extends UIDateElement {
    @Attribute(String) controlId?: string;

    @Child('ui-timepicker.-from') pickerFrom?: UITimePicker;
    @Child('ui-timepicker.-to') pickerTo?: UITimePicker;

    private _fromInputHadUserId = false;
    private _toInputHadUserId = false;

    /**
     * @type {UITimeRange}
     */
    override readonly control: this = this;

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-timerange', {
            TIME_START: 'Time start',
            TIME_END: 'Time end',
        });
    }

    get valueType(): string {
        return UIDateElement.VALUE_TYPES.TIME;
    }

    /**
     * Checks if an id is a generated id (empty or matches Digest.randomId() pattern)
     * @param {string|null|undefined} id
     * @returns {boolean}
     * @private
     */
    isGeneratedId(id: Optional<string>): boolean {
        return !id || /^uid-[0-9a-f]{8}$/i.test(id);
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['control-id'];
    }

    /**
     * Syncs start and end values.
     * @override
     */
    syncControls(): void {
        if (!this.pickerFrom || !this.pickerTo) {
            return;
        }
        if (!this.isValidTime(this.pickerTo.value)) {
            this.pickerFrom.max = this.max;
        } else {
            this.pickerFrom.max = this.pickerTo.value;
        }

        if (!this.isValidTime(this.pickerFrom.value)) {
            this.pickerTo.min = this.min;
        } else {
            this.pickerTo.min = this.pickerFrom.value;
        }
    }

    /**
     * Fires callback when a value of the date-from control is changed.
     * @param {Event} e
     * @private
     */
    handleChangeDateFrom(e: Event): void {
        this.syncControls();
    }

    /**
     * Fires callback when a value of the date-to control is changed.
     * @param {Event} e
     * @private
     */
    handleChangeDateTo(e: Event): void {
        this.syncControls();
    }

    /**
     * Returns the control id that should be used by ui-field for label association.
     * Allows component to control which id is returned to ui-field,
     * rather than ui-field having to query the component's internal DOM structure.
     * Returns the base id for the group element (from control-id or generated).
     * The inner timepickers derive their ids from this base.
     * @returns {string}
     */
    getControlIdForField(): string {
        const existingId = this.getAttribute('id') || this.id;

        if (existingId?.trim() && !this.isGeneratedId(existingId)) {
            return existingId;
        }

        if (this.controlId) {
            this.id = this.controlId;
            this.setAttribute('id', this.controlId);
            return this.controlId;
        }

        const baseId = Digest.randomId();
        this.id = baseId;
        this.setAttribute('id', baseId);
        return baseId;
    }

    /**
     * Sets the relationship between the label of an ancestor ui-field and the host.
     * @private
     */
    updateId(): void {
        const field = this.closest<HTMLElement>('ui-field');
        const labelEl = field?.querySelector<HTMLElement>(
            '.ui-field__label-inner'
        );

        if (!field || !labelEl) {
            return;
        }

        const groupId = this.getControlIdForField();

        if (!labelEl.id && groupId) {
            labelEl.setAttribute('id', `${groupId}-label`);
        }
        this.setAttribute('role', 'group');
        const labelledBy = labelEl.id || (groupId ? `${groupId}-label` : null);
        if (labelledBy) {
            this.setAttribute('aria-labelledby', labelledBy);
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const pickers = this.queryChildren('ui-timepicker');
        const pickerInputs: Array<NullOr<HTMLInputElement>> = [
            pickers[0]
                ? pickers[0].querySelector<HTMLInputElement>('input')
                : null,
            pickers[1]
                ? pickers[1].querySelector<HTMLInputElement>('input')
                : null,
        ];

        const fromId = pickerInputs[0]?.getAttribute('id');
        const toId = pickerInputs[1]?.getAttribute('id');
        this._fromInputHadUserId = !!(
            pickerInputs[0] &&
            fromId?.trim() &&
            !this.isGeneratedId(fromId)
        );
        this._toInputHadUserId = !!(
            pickerInputs[1] &&
            toId?.trim() &&
            !this.isGeneratedId(toId)
        );

        const hostId = this.getAttribute('id') || this.id;
        const hostHadUserId = !!(hostId?.trim() && !this.isGeneratedId(hostId));

        const baseId = hostHadUserId
            ? hostId
            : this.controlId || Digest.randomId();

        if (!hostHadUserId) {
            this.id = baseId;
            this.setAttribute('id', baseId);
        }

        this.detachChildNodes();
        this.insertElements([
            {
                tagName: 'ui-timepicker',
                element: pickers[0],
                classList: {
                    '-from': true,
                },
                children: [
                    {
                        tagName: 'input',
                        element: pickerInputs[0],
                        attributes: {
                            ...((!pickerInputs[0] ||
                                !this._fromInputHadUserId) && {
                                id: `${baseId}-from`,
                            }),
                            'aria-label': UITimeRange.labels.TIME_START,
                            'type': pickerInputs[0]
                                ? pickerInputs[0].getAttribute('type')
                                : 'time',
                            'min': this.min,
                            'max': this.max,
                        },
                    },
                ],
            },
            {
                tagName: 'span',
                attributes: { class: 'ui-timerange__separator' },
            },
            {
                tagName: 'ui-timepicker',
                element: pickers[1],
                classList: {
                    '-to': true,
                },
                children: [
                    {
                        tagName: 'input',
                        element: pickerInputs[1],
                        attributes: {
                            ...((!pickerInputs[1] ||
                                !this._toInputHadUserId) && {
                                id: `${baseId}-to`,
                            }),
                            'aria-label': UITimeRange.labels.TIME_END,
                            'type': pickerInputs[1]
                                ? pickerInputs[1].getAttribute('type')
                                : 'time',
                            'min': this.min,
                            'max': this.max,
                        },
                    },
                ],
            },
        ]);

        // Set derived ids for existing inputs that don't have user-set ids
        if (!this._fromInputHadUserId && this.pickerFrom?.control) {
            this.pickerFrom.control.setAttribute('id', `${baseId}-from`);
        }
        if (!this._toInputHadUserId && this.pickerTo?.control) {
            this.pickerTo.control.setAttribute('id', `${baseId}-to`);
        }
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        // Apply derived ids only when input did not have a user-set id
        const baseId = this.getControlIdForField();
        if (this.pickerFrom?.control && !this._fromInputHadUserId) {
            this.pickerFrom.control.setAttribute('id', `${baseId}-from`);
        }
        if (this.pickerTo?.control && !this._toInputHadUserId) {
            this.pickerTo.control.setAttribute('id', `${baseId}-to`);
        }
        this.updateId();
        this.syncControls();
        if (this.pickerFrom) {
            updateElement(this.pickerFrom, {
                events: {
                    change: (e?: Event | CustomEvent<unknown>) =>
                        this.handleChangeDateFrom(e as Event),
                },
            });
        }
        if (this.pickerTo) {
            updateElement(this.pickerTo, {
                events: {
                    change: (e?: Event | CustomEvent<unknown>) =>
                        this.handleChangeDateTo(e as Event),
                },
            });
        }
    }
}
